import Phaser from "phaser";
import type { Light, Spot } from "../../sim";
import { pixelRatio } from "../display";
import { type Fit, imageToScreen } from "../layout";
import { lightColor, TRICK_COLOR, TRICK_CORE } from "../palette";
import { GLOW_SIZE, TEX } from "./textures";

/** Ticks a light takes to fade in. */
const FADE_IN_TICKS = 10;
/** The light blinks during its last ticks: "hurry, I'm going out". */
const BLINK_TICKS = 45;
/** Halo diameter as a multiple of the spot's diameter, and its minimum on screen in CSS pixels. */
const HALO = 2.8;
const MIN_HALO_PX = 90;
const MIN_CORE_PX = 22;

/**
 * One lit spot: a coloured halo, a bright core and, for golden lights, a turning ring. A trick
 * light is toxic green with a dark core and a jagged ring, and it flickers.
 */
export class LightView {
	private readonly halo: Phaser.GameObjects.Image;
	private readonly core: Phaser.GameObjects.Image;
	private readonly ring: Phaser.GameObjects.Image | null;

	constructor(
		private readonly scene: Phaser.Scene,
		readonly light: Light,
		private readonly spot: Spot,
	) {
		const color = light.trick ? TRICK_COLOR : lightColor(light.color, light.golden);
		this.halo = scene.add.image(0, 0, TEX.glow).setBlendMode(Phaser.BlendModes.ADD).setTint(color);
		this.core = light.trick
			? scene.add.image(0, 0, TEX.glow).setTint(TRICK_CORE)
			: scene.add
					.image(0, 0, TEX.glow)
					.setBlendMode(Phaser.BlendModes.ADD)
					.setTint(Phaser.Display.Color.ValueToColor(color).lighten(30).color);
		this.ring =
			light.golden || light.trick
				? scene.add
						.image(0, 0, light.trick ? TEX.spikes : TEX.ring)
						.setBlendMode(Phaser.BlendModes.ADD)
						.setTint(color)
				: null;
	}

	/** `tick` = the sim tick plus the loop's progress to the next one; `time` = ms for pulsing. */
	update(fit: Fit, tick: number, time: number): void {
		const { light, spot } = this;
		const p = imageToScreen(fit, spot.x, spot.y);
		const age = tick - light.litTick;
		const left = light.outTick - tick;
		const fadeIn = Math.min(1, Math.max(0, age / FADE_IN_TICKS));
		let alpha = fadeIn;
		// Switching on "pops" (starts big) so the eye finds it.
		const pop = 1 + 0.9 * (1 - fadeIn);
		if (left < BLINK_TICKS) alpha *= 0.55 + 0.45 * Math.cos(left * 0.5);
		// A trick light flickers unevenly, like something not quite right.
		if (light.trick)
			alpha *= 0.7 + 0.3 * Math.sin(time * 0.031 + light.id) * Math.sin(time * 0.017);
		const pulse = 1 + 0.1 * Math.sin(time * 0.006 + light.id);
		const d = spot.r * 2 * fit.scale;
		const px = pixelRatio();
		this.halo
			.setPosition(p.x, p.y)
			.setScale((Math.max(d * HALO, MIN_HALO_PX * px) / GLOW_SIZE) * pulse * pop)
			.setAlpha(alpha);
		this.core
			.setPosition(p.x, p.y)
			.setScale(Math.max(d * 1.1, MIN_CORE_PX * px) / GLOW_SIZE)
			.setAlpha(alpha * (light.trick ? 0.9 : 0.6));
		this.ring
			?.setPosition(p.x, p.y)
			.setScale((Math.max(d * 1.7, MIN_HALO_PX * 0.6 * px) / GLOW_SIZE) * (2 - pulse))
			.setRotation(time * (light.trick ? -0.006 : 0.003))
			.setAlpha(alpha);
	}

	/** Gone without being collected: shrink and fade. */
	fadeOut(): void {
		const parts = [this.halo, this.core, this.ring].filter((o) => o !== null);
		this.scene.tweens.add({
			targets: parts,
			alpha: 0,
			scale: 0,
			duration: 250,
			onComplete: () => {
				for (const o of parts) o.destroy();
			},
		});
	}

	destroy(): void {
		this.halo.destroy();
		this.core.destroy();
		this.ring?.destroy();
	}
}
